import {
    Box,
    Container,
    Grid,
    Paper,
    Typography,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TablePagination,
    Stack,
    Tabs,
    Tab,
} from "@mui/material";
import { useCallback, useMemo, useRef, useState } from "react";
import { useModalDetalhePedido } from "../../ModalDetalhePedidoContext";
import { useQuery } from "react-query";
import axiosClient from "../../../../../lib/axios";
import IRequest from "../../../../../types/IRequest";
import { IDataPesquisarPedidos } from "../../../../../types/api/comercial/pesquisar-pedidos";
import DisplayNotFoundInfo from "../../../../../components/DisplayNotFindInfo";
import LoadingApiQuery from "../../../../../components/LoadingApiQuery";
import { IDataPublicos } from "../../../../../types/api/geral/pesquisar-publicos";
import { IPesquisaVendedores } from "../../../../../types/api/geral/pesquisar-vendedores";
import PermissionGuard from "../../../../../components/PermissionGuard";
import moment, { Moment } from "moment";
import ScreenBanner from "../../../../../components/ScreenBanner";
import SearchWithFilters from "../../../../../components/BasicComponents/SearchWithFilters";
import useDebounce from "../../../../../lib/useDebounce";
import ChipList from "../../../../../components/BasicComponents/ChipList";
import { FiltroModal } from "./FiltroModal";
import { formatarValorMonetario } from "../../../../../helpers/masks";

export default function Listagem() {

    const { openModal } = useModalDetalhePedido();

    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const [tab, setTab] = useState<number>(0);
    const [openFilterModal, setOpenFilterModal] = useState(false);
    const [filtros, setFiltros] = useState(defaultPaginationState);
    const [dialogFilters, setDialogFilters] = useState<TFilterState>(defaultFilterState);
    const [palavraChave, setPalavraChave] = useState<string>("");
    const debouncedKeyword = useDebounce(palavraChave);

    const formatFilters = useMemo(() => {
        return {
            ...(dialogFilters.vendedor_dado && { vendedor_id: dialogFilters.vendedor_dado.id }),
            ...(dialogFilters.publico_dado && {
                tipo_publico: dialogFilters.publico_dado.tipo_publico,
                codigo_publico: dialogFilters.publico_dado.codigo_publico
            }),
            ...(dialogFilters.situacoes.length > 0 && { situacoes: dialogFilters.situacoes }),
            codigo_pedido: dialogFilters.codigo_pesquisar || null,
            comprador: dialogFilters.comprador_pesquisar || null,
            condicao_pagamento_id: dialogFilters.condicao_pagamento_id_pesquisar || null,
            nf_expedicao_numero: dialogFilters.nf_expedicao_numero || null,
            emissao_inicio: dialogFilters.data_emissao.inicial?.format('YYYY-MM-DD') || null,
            emissao_fim: dialogFilters.data_emissao.final?.format('YYYY-MM-DD') || null,
            ...(tab === 0 && { status: null }),
            ...(tab === 1 && { status: "em-andamento" }),
            ...(tab === 2 && { status: "finalizados" }),
            ...(tab === 3 && { status: "cancelados" }),
        };
    }, [dialogFilters, tab])

     const { data: preDataContadores } = useQuery({
            queryKey: ["contar-pedidos"],
            queryFn: () => axiosClient.get("comercial/pedidos/contar-pedidos"),
            cacheTime: 0
        });
        const dataContadores: IDataContadorPedidos = useMemo(() => preDataContadores?.data, [preDataContadores])

    const { data, isLoading: isLoadingPedidos } = useQuery({
        queryKey: ["pesquisar-pedidos", filtros, dialogFilters, debouncedKeyword, tab],
        queryFn: () =>
            axiosClient.get("comercial/pesquisar-pedidos", {
                params: {
                    limit: filtros.porPagina,
                    page: filtros.pagina + 1,
                    pesquisar: debouncedKeyword || null,
                    ...formatFilters
                },
            }),
        cacheTime: 0,
    });

    const { reqPedidos, dataPedidos }: IPedidos = useMemo(
        () => ({
            reqPedidos: data?.data,
            dataPedidos: data?.data.data || [],
        }),
        [data]
    );


    const handleChangePage = useCallback(
        (_: React.MouseEvent<HTMLButtonElement> | null, novaPag: number) => {
            setFiltros((prevState) => ({
                ...prevState,
                pagina: novaPag,
            }));
        },
        []
    );

    const handleChangeRowsPerPage = useCallback(
        (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            setFiltros({
                porPagina: parseInt(event.target.value, 10),
                pagina: 0,
            });
        },
        []
    );

    const handleFilterSubmit = (form: TFilterState) => {
        if (Array.isArray(form.situacoes) && form.situacoes.length > 0) {
            setTab(0);
        }
        setDialogFilters(form);
        setFiltros(defaultPaginationState);
    };

    const handleClearFilters = () => {
        setDialogFilters(defaultFilterState);
        setPalavraChave("");
        setFiltros(defaultPaginationState);
    };

    const handleSearch = useCallback(() => {
        setFiltros(defaultPaginationState);
    }, []);

    const handleChangeTabs = (_: React.SyntheticEvent, newValue: number) => {
        setFiltros((prevState) => ({
            ...prevState,
            pagina: 0,
            porPagina: 10,
        }));
        setTab(newValue);
    }

    const chipItems = useMemo(() => {
        const items: string[] = [];

        Object.entries(dialogFilters).forEach(([key, value]) => {
            if (!value) return;

            const label = filtersChipLabels[key as keyof typeof filtersChipLabels];
            if (!label) return;

            if (Array.isArray(value) && value.length > 0) {
                items.push(`${label}: ${value.join(', ')}`);
            }
            else if (typeof value === 'object' && value !== null) {
                if ('inicial' in value && value.inicial) {
                    const dataFinal = value.final ? ` - ${value.final.format('DD/MM/YYYY')}` : '';
                    items.push(`${label}: ${value.inicial.format('DD/MM/YYYY')}${dataFinal}`);
                }
                else if (key === 'publico_dado' && value && 'tipo_publico' in value && 'codigo_publico' in value) {
                    items.push(`${label}: ${value.tipo_publico}${value.codigo_publico} - ${value.nome_pessoa}`);
                } else if ('nome' in value && value.nome) {
                    items.push(`${label}: ${value.nome}`);
                } else if ('razao_social' in value && value.razao_social) {
                    items.push(`${label}: ${value.razao_social}`);
                }
            }
            else if ((typeof value === 'string' || typeof value === 'number') && String(value).trim() !== '') {
                items.push(`${label}: ${value}`);
            }
        });

        if (palavraChave.trim() !== '') {
            items.push(`Pesquisa: ${palavraChave}`);
        }

        return items;
    }, [dialogFilters, palavraChave]);

    const handleDeleteFilter = (item: string) => {
        if (item.startsWith('Pesquisa:')) {
            setPalavraChave("");
            return;
        }

        const newFilters = { ...dialogFilters };

        const [label] = item.split(': ');
        const filterKey = Object.entries(filtersChipLabels).find(
            ([, v]) => v === label
        )?.[0];

        if (filterKey) {
            switch (filterKey) {
                case 'situacoes':
                    newFilters.situacoes = [];
                    break;
                case 'data_emissao':
                    newFilters.data_emissao = { inicial: null, final: null };
                    break;
                case 'vendedor_dado':
                case 'publico_dado':
                    newFilters[filterKey] = null;
                    break;
                case 'codigo_pesquisar':
                case 'comprador_pesquisar':
                case 'condicao_pagamento_id_pesquisar':
                case 'nf_expedicao_numero':
                    newFilters[filterKey] = '';
                    break;
            }
            setDialogFilters(newFilters);
        }
    };

    return (
        <>
            <ScreenBanner title="Comercial" />
            <PermissionGuard permissionId="comercial.listar-pedidos">
                <Box sx={{ background: "#F5F6FA", minHeight: "100vh", py: 4 }}>
                    <Container maxWidth="lg">
                        <Typography textAlign="center" variant="h5" mb={3}>
                            Pedidos
                        </Typography>



                        <Grid size={{ xs: 12, md: 9 }} ref={containerRef}>
                            <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                                <Box sx={{ width: "100%", overflowX: "auto" }}>
                                    <SearchWithFilters
                                        palavraChave={palavraChave}
                                        textFieldProps={{
                                            inputRef: inputRef,
                                            label: "Pesquisar",
                                            placeholder: "Pesquise por número do pedido ou cliente",
                                            size: "medium",
                                        }}
                                        pesquisar={handleSearch}
                                        setTermoPesquisa={setPalavraChave}
                                        handleOpenFilters={() => setOpenFilterModal(true)}
                                    />

                                    <ChipList
                                        items={chipItems}
                                        onDelete={handleDeleteFilter}
                                    />

                                    <Tabs
                                        variant="scrollable"
                                        scrollButtons="auto"
                                        value={tab}
                                        onChange={handleChangeTabs}
                                    >
                                        <Tab label={`Todos (${dataContadores?.total || 0})`} id={TAB_IDS[0]} />
                                        <Tab label={`Em andamento (${dataContadores?.total_em_andamento || 0})`} id={TAB_IDS[1]} />
                                        <Tab label={`Finalizados (${dataContadores?.total_finalizados || 0})`} id={TAB_IDS[2]} />
                                        <Tab label={`Cancelados (${dataContadores?.total_cancelados || 0})`} id={TAB_IDS[3]} />
                                    </Tabs>

                                    {isLoadingPedidos && <LoadingApiQuery extraParams={{ height: 200, alignItems: 'center' }} />}

                                    {!isLoadingPedidos && (
                                        <>
                                            {dataPedidos.length === 0 ? (
                                                <DisplayNotFoundInfo label="Nenhum pedido encontrado" />
                                            ) : (
                                                <>
                                                    <Table>
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell width="25%"><b>Pedido</b></TableCell>
                                                                <TableCell width="25%"><b>Vendedor</b></TableCell>
                                                                <TableCell width="25%"><b>Cliente</b></TableCell>
                                                                <TableCell width="20%" align="right"><b>Valor</b></TableCell>
                                                                <TableCell width="15%"><b>Status</b></TableCell>
                                                                <TableCell width="15%"><b>Data</b></TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {dataPedidos.map((pedido) => (
                                                                <TableRow key={pedido.id} hover>
                                                                    <TableCell onClick={() => openModal(pedido.id.toString())}>
                                                                        <Typography fontWeight="medium" sx={{ color: 'primary.main', fontWeight: 'bold', textDecoration: 'underline', cursor: 'pointer' }}>
                                                                            #{pedido.codigo}
                                                                        </Typography>

                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Typography variant="body2" color="text.secondary">
                                                                            <Stack direction={'column'}>
                                                                                <Typography variant="caption">
                                                                                    {pedido.vendedor}
                                                                                </Typography>
                                                                            </Stack>
                                                                        </Typography>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Typography variant="body2" color="text.secondary">
                                                                            <Stack direction={'column'}>
                                                                                <Typography variant="caption">
                                                                                    {pedido.comprador}
                                                                                </Typography>
                                                                            </Stack>
                                                                        </Typography>
                                                                    </TableCell>
                                                                    <TableCell align="right">
                                                                        <Typography variant="body1" fontWeight="medium">
                                                                            {formatarValorMonetario(pedido.total)}
                                                                        </Typography>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Typography variant="body2" sx={{
                                                                            fontWeight: 'medium'
                                                                        }}>
                                                                            {pedido.situacao}
                                                                        </Typography>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Typography variant="body2">
                                                                            {moment(pedido.created_at).format("DD/MM/YYYY HH:mm")}
                                                                        </Typography>
                                                                    </TableCell>
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>

                                                    <Stack direction="row" justifyContent="flex-end" mt={2}>
                                                        <TablePagination
                                                            component="div"
                                                            count={reqPedidos?.total || 0}
                                                            page={filtros.pagina}
                                                            onPageChange={handleChangePage}
                                                            rowsPerPage={filtros.porPagina}
                                                            onRowsPerPageChange={handleChangeRowsPerPage}
                                                            labelRowsPerPage="Linhas por página"
                                                            rowsPerPageOptions={[5, 10, 25]}
                                                        />
                                                    </Stack>
                                                </>
                                            )}
                                        </>
                                    )}
                                </Box>
                            </Paper>
                        </Grid>
                    </Container>
                </Box>
                {openFilterModal && (
                    <FiltroModal
                        {...{ dialogFilters }}
                        open={openFilterModal}
                        handleClose={() => setOpenFilterModal(false)}
                        handleFilterSubmit={handleFilterSubmit}
                        onClearFilters={handleClearFilters}
                    />
                )}
            </PermissionGuard >
        </>
    )
}

const TAB_IDS = ["Todos", "Em andamento", "Finalizados", "Cancelados"];

const defaultFilterState: TFilterState = {
    codigo_pesquisar: "",
    comprador_pesquisar: "",
    vendedor_dado: null,
    condicao_pagamento_id_pesquisar: "",
    publico_dado: null,
    situacoes: [],
    nf_expedicao_numero: "",
    data_emissao: {
        inicial: null,
        final: null,
    },
};

const defaultPaginationState = {
    pagina: 0,
    porPagina: 10,
}

const filtersChipLabels: { [key in keyof TFilterState]: string } = {
    vendedor_dado: "Vendedor",
    publico_dado: "Público",
    situacoes: "Situação",
    codigo_pesquisar: "Código",
    comprador_pesquisar: "Comprador",
    condicao_pagamento_id_pesquisar: "Cond. Pagamento",
    nf_expedicao_numero: "NF Expedição",
    data_emissao: "Data Emissão",

};

export type TFilterState = {
    codigo_pesquisar: string;
    comprador_pesquisar: string;
    vendedor_dado: IPesquisaVendedores | null;
    condicao_pagamento_id_pesquisar: string;
    publico_dado: IDataPublicos | null;
    situacoes: string[];
    nf_expedicao_numero: string;
    data_emissao: {
        inicial: Moment | null;
        final: Moment | null;
    };
};
interface IPedidos {
    reqPedidos: IRequest<IDataPesquisarPedidos>;
    dataPedidos: IDataPesquisarPedidos[];
}
export interface IDataContadorPedidos {
    total: number,
    total_em_andamento: number,
    total_cancelados: number,
    total_finalizados: number,
}